import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart2,
  Search as SearchIcon,
  Users,
  Building2,
  Newspaper,
  FlaskRound as Flask,
  DollarSign,
  CheckCircle,
  Mail,
  MapPin,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  CircleUser as UserCircle,
  Calendar,
  Inbox,
  FolderOpen,
  Stethoscope,
  ClipboardList
} from 'lucide-react';
import { Logo } from './Logo';
import { useAuthStore } from '../store/auth';

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<any>;
  color: string;
  isAI?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

interface SidebarProps {
  onOpenSettings?: () => void;
}

export default function Sidebar({ onOpenSettings }: SidebarProps) {
  const location = useLocation();
  const { user, signOut } = useAuthStore();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const navGroups: NavGroup[] = [
    {
      title: 'Overview',
      items: [
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, color: 'text-primary' },
        { name: 'Analytics', path: '/analytics', icon: BarChart2, color: 'text-secondary' },
        ...(user?.role === 'admin' ? [{ name: 'Weekly Activity', path: '/weekly-activity', icon: ClipboardList, color: 'text-warning' }] : []),
      ],
    },
    {
      title: 'Sales Pipeline',
      items: [
        { name: 'Deals', path: '/deals', icon: DollarSign, color: 'text-warning' },
        { name: 'Sites', path: '/sites', icon: MapPin, color: 'text-success' },
        { name: 'Principal Investigators', path: '/principal-investigators', icon: Stethoscope, color: 'text-primary' },
        { name: 'Tasks', path: '/tasks', icon: CheckCircle, color: 'text-warning' },
        { name: 'Reports', path: '/reports', icon: BarChart2, color: 'text-primary' },
      ],
    },
    {
      title: 'Communication',
      items: [
        { name: 'Email', path: '/email', icon: Inbox, color: 'text-primary' },
        { name: 'Calendar', path: '/calendar', icon: Calendar, color: 'text-tertiary' },
      ],
    },
    {
      title: 'Data Management',
      items: [
        { name: 'Companies', path: '/companies', icon: Building2, color: 'text-secondary' },
        { name: 'Contacts', path: '/contacts', icon: Users, color: 'text-primary' },
        { name: 'Files', path: '/files', icon: FolderOpen, color: 'text-warning' },
        { name: 'Search', path: '/search', icon: SearchIcon, color: 'text-primary' },
        { name: 'Employee Search', path: '/employees', icon: SearchIcon, color: 'text-tertiary' },
      ],
    },
    {
      title: 'Intelligence',
      items: [
        { name: 'Intelligence Feed', path: '/intelligence', icon: Newspaper, color: 'text-danger', isAI: true },
        { name: 'Study Finder', path: '/studies', icon: Flask, color: 'text-success', isAI: true },
        { name: 'Email AI', path: '/email-generator', icon: Mail, color: 'text-primary', isAI: true },
      ],
    },
  ];

  if (user?.role === 'admin') {
    navGroups.push({
      title: 'Administration',
      items: [
        { name: 'Admin', path: '/admin', icon: Settings, color: 'text-on-surface-variant' },
      ],
    });
  }

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      <div
        className={`fixed left-0 top-0 h-full bg-surface-sidebar border-r border-hairline-border transition-all duration-300 ease-in-out z-40 ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Brand Header */}
          <div className="flex items-center justify-between px-4 py-5 border-b border-hairline-border">
            <Link to="/dashboard" className="flex items-center space-x-3 group">
              <div className="relative">
                <Logo className="h-8 w-auto transform group-hover:scale-110 transition-transform duration-200" />
              </div>
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-on-surface tracking-tight">
                    DevGrow.ai
                  </span>
                  <span className="text-[10px] font-medium text-outline uppercase tracking-wider">
                    Clinical CRM
                  </span>
                </div>
              )}
            </Link>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 rounded-clinical hover:bg-surface-container-high text-outline hover:text-on-surface-variant transition-all duration-200"
            >
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6 scrollbar-thin scrollbar-thumb-outline-variant scrollbar-track-transparent">
            {navGroups.map((group, groupIndex) => (
              <div key={groupIndex}>
                {!isCollapsed && (
                  <h3 className="px-3 mb-2 text-[10px] font-semibold text-outline uppercase tracking-wider">
                    {group.title}
                  </h3>
                )}
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`group relative flex items-center px-3 py-2 rounded-clinical transition-all duration-150 ${
                          active
                            ? 'bg-primary/5 text-primary border-l-2 border-primary'
                            : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high border-l-2 border-transparent'
                        }`}
                      >
                        <Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'text-primary' : 'text-outline group-hover:text-on-surface-variant'}`} />
                        {!isCollapsed && (
                          <span className="ml-3 text-sm font-medium">{item.name}</span>
                        )}
                        {!isCollapsed && item.isAI && (
                          <span className="ml-auto h-1.5 w-1.5 rounded-pill bg-tertiary" title="AI-powered" />
                        )}
                        {isCollapsed && item.isAI && (
                          <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-pill bg-tertiary" />
                        )}
                        {isCollapsed && (
                          <div className="absolute left-full ml-6 px-2 py-1 bg-surface-container-lowest text-on-surface text-sm rounded-clinical opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shadow-clinical-lg border border-hairline-border z-50">
                            {item.name}
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User Section */}
          <div className="border-t border-hairline-border p-3">
            <button
              onClick={onOpenSettings}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-clinical bg-surface-container-high hover:bg-surface-container-highest transition-all duration-200 group cursor-pointer ${
                isCollapsed ? 'justify-center' : ''
              }`}
              title={isCollapsed ? 'Settings' : ''}
            >
              <div className="relative flex-shrink-0">
                <UserCircle className="h-8 w-8 text-primary" />
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-on-surface truncate">{user?.email}</p>
                  <p className="text-xs text-outline capitalize">Click for settings</p>
                </div>
              )}
              {!isCollapsed && (
                <Settings className="h-4 w-4 text-outline group-hover:text-on-surface-variant transition-colors flex-shrink-0" />
              )}
              {isCollapsed && (
                <div className="absolute left-full ml-6 px-2 py-1 bg-surface-container-lowest text-on-surface text-sm rounded-clinical opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shadow-clinical-lg border border-hairline-border z-50">
                  Settings
                </div>
              )}
            </button>
            <button
              onClick={() => signOut()}
              className={`mt-2 w-full flex items-center px-3 py-2.5 rounded-clinical text-on-surface-variant hover:text-danger hover:bg-danger/5 transition-all duration-200 group ${
                isCollapsed ? 'justify-center' : 'justify-start'
              }`}
            >
              <LogOut className="h-5 w-5 flex-shrink-0 group-hover:text-danger" />
              {!isCollapsed && <span className="ml-3 text-sm font-medium">Sign Out</span>}
            </button>
          </div>
        </div>
      </div>

      {isCollapsed ? <div className="w-20"></div> : <div className="w-64"></div>}
    </>
  );
}
